import { Button } from '@app/components/various/Button';
import { Remove, ShoppingBag } from '@app/components/various/Icons';
import { getAreBackordersEnabled, getSoldOutLabel } from '@app/ducks/config';
import { getHasAvailableVariants, getNativeVariants } from '@app/ducks/helpers';
import { getHasOptimisticUpdatesByProductId, getOrderDetails } from '@app/ducks/order';
import { useToggleInOrder } from '@app/utils/hooks/useToggleInOrder';
import { Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  productDetails: Product.Standard;
  deliveryWindowIds?: string[];
}

export const ProductDetailsButton = ({ productDetails, deliveryWindowIds }: Props) => {
  const { t } = useTranslation(['common', 'selections']);

  const activeProductId = productDetails.product;
  const nativeVariants = getNativeVariants(productDetails, productDetails.product);
  const hasAvailableVariants = getHasAvailableVariants(nativeVariants, deliveryWindowIds);

  const orderDetails = useSelector(getOrderDetails);
  const backordersEnabled = useSelector(getAreBackordersEnabled);
  const isMutatingUnits = useSelector(getHasOptimisticUpdatesByProductId(activeProductId));
  const soldOutLabel = useSelector(getSoldOutLabel);

  const { isAdding, isRemoving, isInOrder, hasUnits, toggleInOrder } = useToggleInOrder({ product: productDetails });
  const isLoading = isAdding || isRemoving || isMutatingUnits;
  const isSoldOut = !hasAvailableVariants && !backordersEnabled;

  const title = React.useMemo(() => {
    if (isAdding || (!hasUnits && isMutatingUnits)) {
      return t('common:adding');
    }

    if (isMutatingUnits) {
      return t('common:updating');
    }

    if (isRemoving) {
      return t('common:removing');
    }

    if (isInOrder) {
      return t('selections:remove_from_selection');
    }

    return isSoldOut ? soldOutLabel : t('selections:add_to_selection');
  }, [isAdding, hasUnits, isMutatingUnits, isRemoving, isInOrder, isSoldOut, soldOutLabel, t]);

  const buttonIcon = React.useMemo(() => {
    if (isSoldOut) {
      return undefined;
    }

    if (!isInOrder) {
      return ShoppingBag;
    }

    return Remove;
  }, [isInOrder, isSoldOut]);

  if (!orderDetails.order.canModify) {
    return null;
  }

  const isDisabled = !isInOrder && isSoldOut;

  const buttonProps =
    isInOrder ? ({ color: 'secondary', variant: 'bordered' } as const) : ({ color: 'primary', variant: 'solid' } as const);

  return (
    <Button icon={buttonIcon} disabled={isDisabled} isLoading={isLoading} isFluid {...buttonProps} onClick={toggleInOrder}>
      {title}
    </Button>
  );
};
